import { useCallback, useState } from 'react';
import { incomingInvoice as incomingInvoiceApi } from 'utilities/api';
import { useToast } from '../../../components/ToastContainer';
import useApiCall from '../helpers/useIntegrationApiHelpers';

const useIncomingInvoiceApi = () => {
  const [loading, setLoading] = useState(false);
  const orgId = localStorage.getItem('selectedOrganizationId');

  const apiCall = useApiCall({
    setLoading,
    toast: useToast(),
    dispatch: () => {} // This is like that because global disptach causing undesired redirects
  });

  const listIncomingInvoices = useCallback(
    params => {
      const { promise } = incomingInvoiceApi
        .createChildApi({
          action: 'integrations/incoming-invoice'
        })
        .fetch(null, params, {
          headers: {
            'cwf-context': JSON.stringify({
              organization_id: orgId
            })
          }
        });

      setLoading(true);

      return apiCall(promise, {
        errorTitle: 'Unable to list Invoices',
        errorContent:
          'An error prevented to list existing Incoming Invoices. Please try again.'
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, apiCall]
  );

  const importIncomingInvoices = useCallback(
    ({ seasonId, ids, excludedProductsIds = [] }) => {
      const { promise } = incomingInvoiceApi
        .createChildApi({
          action: 'integrations/incoming-invoice/import'
        })
        .update(
          null,
          {
            ids,
            seasonId,
            excludedProductsIds
          },
          {
            headers: {
              'cwf-context': JSON.stringify({
                organization_id: orgId
              })
            }
          }
        );

      setLoading(true);

      return apiCall(promise, {
        errorTitle: 'Unable to create incoming invoices',
        errorContent:
          'An error prevented to import incoming invoices. Please try again'
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, apiCall]
  );

  const getMatches = useCallback(
    ({ vendor, matchTypes = [] }) => {
      const { promise } = incomingInvoiceApi
        .createChildApi({
          action: 'integrations/incoming-invoice/match'
        })
        .fetch(
          null,
          {
            vendor,
            types: matchTypes.join(',')
          },
          {
            headers: {
              'cwf-context': JSON.stringify({
                organization_id: orgId
              })
            }
          }
        );

      setLoading(true);

      return apiCall(promise, {
        errorTitle: 'Unable to retrieve matches',
        errorContent:
          'An error prevented to retrieve matches. Please try again.'
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, apiCall]
  );

  const createMatches = useCallback(
    ({ vendor, matches }) => {
      const { promise } = incomingInvoiceApi
        .createChildApi({
          action: 'integrations/incoming-invoice/match'
        })
        .create(
          matches.map(match => ({ ...match, vendor })),
          {
            headers: {
              'cwf-context': JSON.stringify({
                organization_id: orgId
              })
            }
          }
        );

      setLoading(true);

      return apiCall(promise, {
        errorTitle: 'Unable to create matches',
        errorContent: 'An error prevented to create match. Please try again'
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, apiCall]
  );

  const updatesUnitsUOM = useCallback(
    ({ unitsUpdates }) => {
      /**
       * Units Updates should have this format
       * [{
            "id": "20d59fa9-d1e6-4345-a5b1-cfd9356a471b", <- Invoice Id
            "productsUnits": [
                {
                    "id": "20d59fa9-d1e6-4345-a5b1-cfd9356a471b", <- Product invoice Id
                    "unit": "lb", <- Unit
                    "value": 100 <- Quantity
                }
            ]
        }]
       */

      const { promise } = incomingInvoiceApi
        .createChildApi({
          action: 'integrations/incoming-invoice/update-products-units'
        })
        .update(null, unitsUpdates, {
          headers: {
            'cwf-context': JSON.stringify({
              organization_id: orgId
            })
          }
        });

      setLoading(true);

      return apiCall(promise, {
        errorTitle: 'Unable to unit matches',
        errorContent:
          'An error prevented to create unit match. Please try again'
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, apiCall]
  );

  return {
    listIncomingInvoices,
    importIncomingInvoices,
    createMatches,
    getMatches,
    updatesUnitsUOM,
    loading
  };
};

export default useIncomingInvoiceApi;
